// SpatialFilterModal.jsx – Dynamic spatial filter modal (aligned with new backend APIs)
// ------------------------------------------------------------------
// UPDATED BACKEND ENDPOINTS (now live)
// ------------------------------------------------------------------
// 1. GET /api/spatial/filters/
//    Description: Returns unique agriculture names and crop types.
//    Response: { keshte: string[], croptype: string[] }
//
// 2. GET /api/spatial/options/?keshte=<name>&croptype=<type>
//    Description: Returns variety, age, and status for the selected agriculture (and optional crop).
//    Response: { variety: string[], age: string[], status: string[] }
//
// 3. GET /api/spatial/units/?keshte=<name>&type=<unit_type>&croptype=<...>&variety=<...>&age=<...>&status=<...>
//    Description: Returns unit names for the given operational unit type, filtered by other criteria.
//    Response: { units: string[] }
//
// 4. POST /api/spatial/polygon/
//    Description: Returns GeoJSON polygon(s) for farms matching the filter criteria.
//    Request body: { keshte, type, unit, croptype?, variety?, age?, status? }
//    Response: FeatureCollection (GeoJSON)
//
// All endpoints require Authorization: Bearer <token>
// ------------------------------------------------------------------

import React, { useState, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import { Button, MenuItem, Select, IconButton, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import "./SpatialFilterModal.css";

// ------------------------------------------------------------------
// API helper – all calls go through this base URL
// ------------------------------------------------------------------
const API_BASE =
  process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "") ||
  "http://178.131.60.38:8000/api";

// Simple fetch wrapper that attaches the auth token
const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("authToken");
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: token ? `Bearer ${token}` : "",
    },
  });
};

// ------------------------------------------------------------------
// Styled Components (unchanged)
// ------------------------------------------------------------------
const StyledSelect = styled(Select)({
  fontFamily: "'Yekan Bakh FaNum Medium', sans-serif",
  fontSize: "0.95rem",
  backgroundColor: "#F8FAF9",
  borderRadius: "8px",
  height: "48px",
  "& fieldset": { border: "1px solid #E0E0E0" },
  "&:hover": { backgroundColor: "#F0F2F1" },
  "&:hover fieldset": { borderColor: "#B0B0B0 !important" },
  "&.Mui-focused fieldset": {
    borderColor: "#344E41 !important",
    borderWidth: "1px !important",
  },
  "& .MuiSelect-select": { padding: "12px 14px", textAlign: "right" },
  "& .MuiSelect-icon": {
    right: "auto !important",
    left: "8px",
    color: "#344E41",
  },
});

const StyledMenuItem = styled(MenuItem)({
  fontFamily: "'Yekan Bakh FaNum Medium', sans-serif",
  direction: "rtl",
  justifyContent: "flex-start",
  "&:hover": { backgroundColor: "#F0F5F3", color: "#344E41" },
  "&.Mui-selected": { backgroundColor: "#DDE5E1", color: "#344E41" },
  "&.Mui-selected:hover": { backgroundColor: "#DDE5E1" },
});

const StyledTextField = styled(TextField)({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: "48px",
    backgroundColor: "#F8FAF9",
    borderRadius: "8px",
    fontFamily: "'Yekan Bakh FaNum Medium', sans-serif",
    "& fieldset": { borderColor: "#E0E0E0" },
    "&:hover fieldset": { borderColor: "#B0B0B0" },
    "&.Mui-focused fieldset": { borderColor: "#344E41" },
  },
  "& input": { textAlign: "right", direction: "rtl" },
});

const FilterField = ({ label, children }) => (
  <div className="filter-wrapper">
    <label className="filter-label">{label}</label>
    {children}
  </div>
);

const SpatialFilterModal = ({
  isOpen,
  onClose,
  onApplyFilter,
  initialFilter = null,
}) => {
  const overlayRef = useRef();

  // ------------------------------------------------------------------
  // STATE – all dropdown options are fetched dynamically
  // ------------------------------------------------------------------
  const [keshteOptions, setKeshteOptions] = useState([]);
  const [croptypeOptions, setCroptypeOptions] = useState([]);
  const [productionOptions, setProductionOptions] = useState([
    "farm",
    "block",
    "office",
    "management",
  ]);
  const [varietyOptions, setVarietyOptions] = useState([]);
  const [ageOptions, setAgeOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);

  const [optionsLoading, setOptionsLoading] = useState(false);
  const [applying, setApplying] = useState(false); // loading for polygon fetch

  const [filters, setFilters] = useState({
    keshte: "",
    production: "farm",        // default unit type is now "farm" (matches API)
    department: "",
    productType: "",
    number: "",
    age: "",
    status: "",
    searchQuery: "",
  });

  // ------------------------------------------------------------------
  // FETCH INITIAL OPTIONS (keshte & croptype) when the modal opens
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;

    const fetchInit = async () => {
      setOptionsLoading(true);
      try {
        const res = await authFetch(`${API_BASE}/spatial/filters/`);
        if (!res.ok) throw new Error("Failed to fetch spatial filters");
        const data = await res.json();
        setKeshteOptions(data.keshte || []);
        setCroptypeOptions(data.croptype || []);
        setProductionOptions(
          data.types?.length
            ? data.types
            : data.available_unit_types?.length
            ? data.available_unit_types
            : ["farm", "block", "office", "management"]
        );
      } catch (err) {
        console.error("Spatial filters fetch failed:", err);
        setKeshteOptions([]);
        setCroptypeOptions([]);
        setProductionOptions(["farm", "block", "office", "management"]);
      } finally {
        setOptionsLoading(false);
      }
    };

    fetchInit();
  }, [isOpen]);

  // ------------------------------------------------------------------
  // FETCH CASCADING OPTIONS (variety, age, status) when keshte changes
  // Optionally filtered by croptype if selected
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!filters.keshte) {
      setVarietyOptions([]);
      setAgeOptions([]);
      setStatusOptions([]);
      return;
    }

    const params = new URLSearchParams();
    params.set("keshte", filters.keshte);
    if (filters.productType) params.append("croptype", filters.productType);
    const qs = params.toString().replace(/\+/g, "%20");

    const fetchOptions = async () => {
      try {
        const res = await authFetch(`${API_BASE}/spatial/options/?${qs}`);
        if (!res.ok) throw new Error("Failed to fetch spatial options");
        const data = await res.json();
        setVarietyOptions(data.variety || []);
        setAgeOptions(data.age || []);
        setStatusOptions(data.status || []);
      } catch (err) {
        console.error("Fetching spatial options failed:", err);
        setVarietyOptions([]);
        setAgeOptions([]);
        setStatusOptions([]);
      }
    };

    fetchOptions();
  }, [filters.keshte, filters.productType]);

  // ------------------------------------------------------------------
  // FETCH UNIT OPTIONS when keshte, production, and any other filters change
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!filters.keshte || !filters.production) {
      setUnitOptions([]);
      return;
    }

    const params = new URLSearchParams({
      keshte: filters.keshte,
      type: filters.production,
    });
    if (filters.productType) params.append("croptype", filters.productType);
    if (filters.number) params.append("variety", filters.number);
    if (filters.age) params.append("age", filters.age);
    if (filters.status) params.append("status", filters.status);

    const fetchUnits = async () => {
      try {
        const res = await authFetch(`${API_BASE}/spatial/units/?${params}`);
        if (!res.ok) throw new Error("Failed to fetch spatial units");
        const data = await res.json();
        setUnitOptions(data.units || []);
      } catch (err) {
        console.error("Fetching units failed:", err);
        setUnitOptions([]);
      }
    };

    fetchUnits();
  }, [filters.keshte, filters.production, filters.productType, filters.number, filters.age, filters.status]);

  // ------------------------------------------------------------------
  // INITIAL FILTER PROP (if any)
  // ------------------------------------------------------------------
  useEffect(() => {
    if (initialFilter) {
      // Normalize incoming filter values: treat Persian 'همه' or 'all' as empty (meaning 'any')
      const normalize = (v) => (v === "همه" || v === "all" ? "" : v);
      const cleaned = {};
      Object.keys(initialFilter).forEach((k) => {
        cleaned[k] = normalize(initialFilter[k]);
      });
      setFilters((prev) => ({ ...prev, ...cleaned }));
    }
  }, [initialFilter]);

  // ------------------------------------------------------------------
  // MODAL CLOSE LOGIC
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (overlayRef.current === e.target) onClose();
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, onClose]);

  // ------------------------------------------------------------------
  // FILTER CHANGE HANDLERS
  // ------------------------------------------------------------------
  const handleFilterChange = (field, value) => {
    // normalize 'همه' to empty
    const val = value === "همه" ? "" : value;
    setFilters((prev) => {
      const newState = { ...prev, [field]: val };
      if (field === "production") {
        newState.department = "";
        newState.searchQuery = "";
      }
      if (field === "keshte") {
        newState.department = "";
        newState.searchQuery = "";
        newState.number = "";
        newState.age = "";
        newState.status = "";
        newState.productType = "";
      }
      return newState;
    });
  };

  // ------------------------------------------------------------------
  // APPLY – fetch the polygon(s) matching the filters and pass geometry up
  // ------------------------------------------------------------------
  const handleApply = async () => {
    // Build clean filter object
    const cleanFilters = {};
    if (filters.keshte) cleanFilters.keshte = filters.keshte;
    if (filters.production) cleanFilters.type = filters.production;
    // unit: for department or searchQuery (the selected unit or typed value)
    if (filters.department) cleanFilters.unit = filters.department;
    else if (filters.searchQuery) cleanFilters.unit = filters.searchQuery;
    if (filters.productType) cleanFilters.croptype = filters.productType;
    if (filters.number) cleanFilters.variety = filters.number;
    if (filters.age) cleanFilters.age = filters.age;
    if (filters.status) cleanFilters.status = filters.status;

    // At minimum we need keshte, type, and unit
    if (!cleanFilters.keshte || !cleanFilters.type || !cleanFilters.unit) {
      alert("لطفاً کشت و صنعت، واحد عملیاتی و نام بخش را انتخاب کنید.");
      return;
    }

    setApplying(true);
    try {
      const res = await authFetch(`${API_BASE}/spatial/polygon/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cleanFilters),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Failed to load polygon");
      }
      const geoJSON = await res.json(); // FeatureCollection

      onApplyFilter?.({ filters: cleanFilters, geoJSON });
      onClose();
    } catch (err) {
      console.error("Polygon load failed:", err);
      alert(`خطا در بارگذاری چندضلعی: ${err.message}`);
    } finally {
      setApplying(false);
    }
  };

  const handleClear = () => {
    setFilters({
      keshte: "",
      production: "farm",
      department: "",
      productType: "",
      number: "",
      age: "",
      status: "",
      searchQuery: "",
    });
    onApplyFilter?.(null);
  };

  if (!isOpen) return null;

  const renderSelect = (
    value,
    onChange,
    options,
    placeholder = "انتخاب کنید",
  ) => (
    <StyledSelect
      value={value}
      onChange={onChange}
      displayEmpty
      renderValue={(selected) => {
        if (!selected)
          return <span style={{ color: "#aaa" }}>{placeholder}</span>;
        return selected;
      }}
      IconComponent={KeyboardArrowDownIcon}
      MenuProps={{
        sx: { zIndex: 2200 },
        PaperProps: {
          sx: {
            borderRadius: "12px",
            marginTop: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          },
        },
      }}
    >
      <StyledMenuItem value="">
        <em>همه</em>
      </StyledMenuItem>
      {options.map((opt) => (
        <StyledMenuItem key={opt} value={opt}>
          {opt}
        </StyledMenuItem>
      ))}
    </StyledSelect>
  );

  const showDepartment =
    filters.production === "office" || filters.production === "management";
  const showSearch =
    filters.production === "block" || filters.production === "farm";

  return (
    <div className="spatial-filter-modal-overlay" ref={overlayRef}>
      <div className="spatial-filter-modal">
        <div className="modal-header">
          <h2 className="modal-title">فیلتر مکانی</h2>
          <IconButton onClick={onClose} size="small" sx={{ color: "#999" }}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className="modal-content">
          {optionsLoading && <p>در حال بارگذاری گزینه‌ها…</p>}

          <div className="filters-grid">
            {/* کشت و صنعت */}
            <FilterField label="کشت و صنعت">
              {renderSelect(
                filters.keshte,
                (e) => handleFilterChange("keshte", e.target.value),
                keshteOptions,
              )}
            </FilterField>

            {/* واحد عملیاتی – options now match API types: farm, block, office, management */}
            <FilterField label="واحد عملیاتی">
              {renderSelect(
                filters.production,
                (e) => handleFilterChange("production", e.target.value),
                productionOptions,
              )}
            </FilterField>

            {/* نام بخش (dropdown or search) */}
            {(showDepartment || showSearch) && (
              <div className="span-2">
                {showDepartment && (
                  <FilterField label="نام بخش">
                    {renderSelect(
                      filters.department,
                      (e) => handleFilterChange("department", e.target.value),
                      unitOptions,
                    )}
                  </FilterField>
                )}
                {showSearch && (
                  <FilterField
                    label={
                      filters.production === "block"
                        ? "نام/کد بلوک"
                        : "نام/کد مزرعه"
                    }
                  >
                    <StyledTextField
                      value={filters.searchQuery}
                      onChange={(e) =>
                        handleFilterChange("searchQuery", e.target.value)
                      }
                      placeholder="جستجو کنید..."
                    />
                  </FilterField>
                )}
              </div>
            )}

            {/* نوع محصول */}
            <FilterField label="نوع محصول">
              {renderSelect(
                filters.productType,
                (e) => handleFilterChange("productType", e.target.value),
                croptypeOptions,
              )}
            </FilterField>

            {/* رقم */}
            <FilterField label="رقم">
              {renderSelect(
                filters.number,
                (e) => handleFilterChange("number", e.target.value),
                varietyOptions,
              )}
            </FilterField>

            {/* سن کشت */}
            <FilterField label="سن کشت">
              {renderSelect(
                filters.age,
                (e) => handleFilterChange("age", e.target.value),
                ageOptions,
              )}
            </FilterField>

            {/* وضعیت */}
            <FilterField label="وضعیت">
              {renderSelect(
                filters.status,
                (e) => handleFilterChange("status", e.target.value),
                statusOptions,
              )}
            </FilterField>
          </div>
        </div>

        <div className="modal-actions">
          <Button
            variant="contained"
            color="primary"
            disableElevation
            onClick={handleApply}
            disabled={applying}
            sx={{ minWidth: 160 }}
          >
            {applying ? (
              "در حال بارگذاری..."
            ) : (
              <>
                <CheckIcon sx={{ fontSize: 18, mr: 0.75 }} /> اعمال فیلترها
              </>
            )}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleClear}
            sx={{ minWidth: 140, color: "#344E41", borderColor: "#dce8e1" }}
            startIcon={<FilterAltOffIcon sx={{ fontSize: 18 }} />}
          >
            حذف فیلتر
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SpatialFilterModal;